import React, { useEffect, useState } from "react";
import { convertPascalCaseToWords, Dict, FilterValueType, getFilters, Lead, LeadDetails, LeadQuality, ListHeader, ListHeaderSearch, ListSort, ListTable, ListTableColumn, PaginatedApiResponse, QueryFilter, SortOrder,  } from "../../../../core";
import moment from "moment";
import queryString, { ParsedQuery } from "query-string";
import {  useLocation } from "react-router";
import api from "../../../../api";
import { useDataLoader } from "react-remote-data-hooks";
import { toastr } from "react-redux-toastr";
import { CrmAgentColumn, EmptyPersonColumn, SalesPersonColumn } from "../../../../components/list-columns";
import DateRangeListFilter, { IDateRange } from "../../../../core/components/list-filters/DateRangeListFilter";
import Pagination from "../../../../core/components/pagination/Pagination";
import { CompanyAvatar } from "../../../../components/render-file/CompanyAvatar";
import userNoImage from "../../../../assets/images/user-noimage.png";
import { uniqBy } from "lodash";
import { ContainerLoader } from "../../../../components/loader";
import DeleteIcon from "../../../../assets/images/delete-icon.svg";
import { LeadMergeConfirmation } from "./lead-merge-confirmation";
import {LeadColumns, SelectedLeadColumns } from "./lead-merge-table-header";

interface LeadMergeProps {
  onReload?: () => void;
  onClose: () => void;
  className?: string;
  leadDetails?: LeadDetails;
}

const LeadMerge: React.FunctionComponent<LeadMergeProps> = ({ onClose, className, leadDetails, onReload }) => {
  const params = useLocation().search;
  const [search, setSearch] = useState("");
  const [showSelectedLeads, setSelectedLeads] = useState(false);
  const [confirmation, setConfirmation] = useState<boolean>(false);

  const loadFromQuery = () => {
    const queryFilter: QueryFilter = {};
    const queries: ParsedQuery<string> = queryString.parse(params);
    queryFilter.query = queries.query as string;
    if (queries.filters) {
      queryFilter.filters = JSON.parse(queries.filters?.toString());
    }
    if (queries.page) {
      queryFilter.page = Number(queries.page || 1);
    }
    if (queries.perPage) {
      queryFilter.perPage = Number(queries.perPage || 1);
    }
    if (queries.startDate) {
      queryFilter.startDate = moment(queries.startDate as string).toDate();
    }
    if (queries.endDate) {
      queryFilter.endDate = moment(queries.endDate as string).toDate();
    }

    if (queries.sort) {
      queryFilter.sort = JSON.parse(queries.sort?.toString());
    }

    return queryFilter;
  };

  const [filters, setFilters] = useState<Dict<FilterValueType>>(
    loadFromQuery()?.filters || {}
  );
  const [sort, setSort] = useState<ListSort>({
    sortBy: loadFromQuery()?.sort?.sortBy || "createdAt",
    sortOrder: loadFromQuery()?.sort?.sortOrder || SortOrder.DESC,
  });
  const [dateFilters, setDateFilters] = useState<IDateRange>({
    startDate: loadFromQuery()?.startDate,
    endDate: loadFromQuery()?.endDate,
  });
  const [page, setPage] = useState(loadFromQuery()?.page || 1);
  const [perPage, setPerPage] = useState(loadFromQuery()?.perPage || 20);

  const { data, loading, loaded, reload } = useDataLoader<
    PaginatedApiResponse<Lead> & { lastUpdatedAt: Date }
  >(
    (
      searchTerm: string | undefined,
      searchFilters: Dict<FilterValueType>,
      currentPage: number,
      pageCount: number,
      searchOrder?: ListSort,
      dates?: IDateRange
    ) =>
      api.lead.getLeads({
        fromDate: dates?.startDate
          ? moment(dates?.startDate).startOf("day").toDate()
          : undefined,
        toDate: dates?.endDate
          ? moment(dates?.endDate).endOf("day").toDate()
          : undefined,
        ...getFilters(loadFromQuery()?.filters || searchFilters),
        page: currentPage,
        query: loadFromQuery()?.query || searchTerm,
        sortBy: searchOrder?.sortBy,
        sortOrder: searchOrder?.sortOrder,
        perPage: pageCount,
        isDeal: false,
      }),
    [search, filters, page, perPage, sort, dateFilters]
  );

  useEffect(() => {
    if (loaded) {
      setPage(1);
      reload(search, filters, 1, perPage, sort, dateFilters);
    }
  }, [search, filters, sort, perPage, dateFilters]);

  useEffect(() => {
    if (loaded) {
      reload(search, filters, page, perPage, sort, dateFilters);
    }
  }, [page]);

  useEffect(() => {
    const filterResonse = loadFromQuery();

    if (filterResonse.query) {
      setSearch(filterResonse.query as string);
    }
    if (filterResonse.filters) {
      setFilters(filterResonse.filters);
    }
    if (filterResonse.page) {
      setPage(filterResonse.page);
    }
    if (filterResonse.perPage) {
      setPerPage(filterResonse.perPage);
    }

    if (filterResonse.startDate && filterResonse.endDate) {
      setDateFilters({
        startDate: filterResonse.startDate,
        endDate: filterResonse.endDate,
      });
    }
  }, []);

  const [selected, setSelected] = useState<Lead[]>([]);
  const [selectedLeadsForMerge, setSelectedLeadsForMerge] = useState<Lead[]>([]);

  const selectedColumns = [
    ...SelectedLeadColumns,
    <ListTableColumn
      label=""
      source=""
      column=""
      settingsGroup=""
      formatContent={(lead) =>
        <div onClick={() => {
          setSelectedLeadsForMerge((prev) => prev.filter((leadInfo) => leadInfo?._id !== lead._id));
        }} role={'button'} className="text-primary fa-underline"><img src={DeleteIcon} alt="delete Icon" /></div>
      }
      cssClass="right-0 position-sticky bg-white width-40 delete-box"
    ></ListTableColumn>,
  ];

  const headerActions = [
    <ListHeaderSearch
      placeholder="Customer Name / Mobile/ Email"
      serachTerm={search}
      onSearch={(val: string) => {
        setSearch(val);
      }}
    />,
    <DateRangeListFilter
      default={dateFilters}
      onChange={(d?: IDateRange) => {
        setDateFilters({
          startDate: d?.startDate,
          endDate: d?.endDate,
        });
      }}
    />,
  ];

  const onSubmit = async () => {
    if (selectedLeadsForMerge.length < 1) {
      toastr.error("Error", 'Please select at least 2 leads to merge');
      return;
    } else {
      const response = await api.lead.mergeLeads(selectedLeadsForMerge?.map(item => item._id || '') || [], leadDetails?._id || '');
      if (response.status === 200) {
        toastr.success("Success", `Leads Merged Successfully`);
        onReload?.()
        onClose()
      } else {
        toastr.error("Error", 'Something went wrong');
      }
    }
  }

  return (
    <div className="modal-open">
      <div className="detail-popup bg-white position-fixed top-0 w-100 left-0 h-100 overflow-auto in" style={{ zIndex: 9999 }}>
        <div className="container-fluid bg-gray-100 border-bottom py-3 position-sticky top-0 zIndex-99">
          <div className="row align-items-center">
            <div
              className="col-auto"
              onClick={() => {
                onClose?.();
              }}
            >
              <i className="icon-close text-gray-500 font-xxl cursor-pointer"></i>
            </div>
            <div className="col">
              <h5 className="mb-0 py-2">Merge lead</h5>
            </div>
          </div>
        </div>
        <div className={`${className || ""} merge-wrapper`}>
          <div className="row h-100 mx-0">
            <div className="col-sm-6 col-12  border-right px-0 merge-lead-left-wrap">
              <div className="border-bottom px-4 ">
                <div>
                  <ListHeader additionalRows={[]} actions={headerActions}>
                    <h5 className="font-normal font-weight-bold mb-0">Select Lead</h5>
                  </ListHeader>
                </div>
              </div>

              <div className="p-4">
                {loading ?
                  <div className="w-100 h-50 p-5">
                    <ContainerLoader></ContainerLoader>
                  </div>
                  :
                  <>
                    {loaded && !loading && (
                      <ListTable
                        data={data?.data.filter(item => item._id !== leadDetails?._id)}
                        showSelectOption={!!data?.data.filter(item => item._id !== leadDetails?._id).length}
                        selectedValues={selected.map(item => item._id || '')}
                        keepSelectedIds={selected.map(item => item._id || '')}
                        onSelect={(ids: string[]) => {
                          const temp = [...selected, ...data?.data].filter(item => ids.includes(item._id || ''))
                          setSelected(uniqBy(temp, '_id'))
                        }}
                        onSort={(sortKey: string, sortOrder?: SortOrder) => {
                          setSort({ sortBy: sortKey, sortOrder });
                        }}
                        sort={sort}
                        noMargin
                      >
                        {LeadColumns}
                      </ListTable>
                    )}
                  </>}

              </div>
              <div className="button-bottom-merge left-0" style={{ width: "49.4%" }}>
                <div>
                  <Pagination
                    currentPage={page}
                    totalPages={data?.totalPages}
                    onPageChange={(pg: number) => {
                      setPage(pg);
                    }}
                    perPage={perPage}
                    onPerPageChange={(pg: number) => {
                      setPerPage(pg);
                    }}
                    count={data?.count}
                    label="leads"
                  />
                </div>
                <div className="d-flex justify-content-end mt-1">
                  <button className="btn btn-primary" style={{ minWidth: "136px" }} onClick={() => {
                    setSelectedLeads(true)
                    setSelectedLeadsForMerge(selected)
                  }}
                    disabled={selected.length <= 0}
                  >Add</button>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-12 px-0 merge-lead-right-wrap">
              <div className="border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                <div className="py-sm"><h5 className="font-normal font-weight-bold mb-0" style={{ lineHeight: '1.15' }}>Selected Leads ({selectedLeadsForMerge.length})</h5></div>
              </div>
              <div className="mt-3 pt-1">
                <div className="lead-merge-property-enquiry mx-auto">
                  <div className="user-details">
                    <div className="head p-3">
                      <h4 className="font-base text-dark mb-0">{leadDetails?.title}</h4>
                      <span className="font-xs text-muted ">{moment(leadDetails?.createdAt).format(
                        "DD/MMM/YYYY"
                      )}</span>
                    </div>
                    <div className="details p-3">
                      <div className="d-flex hhs">
                        <figure>
                          {leadDetails?.company ?
                            leadDetails?.company?.logo ?
                              <CompanyAvatar
                                id={leadDetails?.company?._id}
                                className="img-thumbnail rounded-circle w-100 h-100"
                                alt={leadDetails?.company?.name}
                                title={leadDetails?.company?.name}
                                defaultImage={userNoImage}
                                imageUrl={leadDetails?.company?.logo?.location}
                                image={leadDetails?.company?.logo}
                              ></CompanyAvatar>
                              :
                              <i className="icon-building font-lg"></i>
                            :
                            <CompanyAvatar
                              className="img-thumbnail  rounded-circle w-100 h-100"
                              defaultImage={userNoImage}
                            ></CompanyAvatar>
                          }
                        </figure>

                        <figcaption className="ml-3">
                          <h6 className="font-normal font-weight-semibold">{leadDetails?.company?.name || leadDetails?.customer?.fullName}</h6>
                          <p className="font-sm mb-0">{leadDetails?.company?.primaryPhone || leadDetails?.customer?.primaryPhone}{" "}<span> <span className="d-inline-block rounded-circle mx-1" style={{ width: "3px", height: "3px", backgroundColor: "#6F7171", verticalAlign: "middle" }}></span>{" "}{leadDetails?.company?.primaryEmail || leadDetails?.customer?.primaryEmail}</span></p>
                        </figcaption>
                      </div>
                    </div>
                  </div>
                  <div className="text-center pt-4 mt-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="37" viewBox="0 0 25 37" fill="none">
                      <path d="M10.7005 6.80635L3.17543 14.3318C2.80779 14.6992 2.37685 14.8778 1.88262 14.8676C1.3884 14.8573 0.957595 14.6686 0.590219 14.3012C0.249446 13.9335 0.073955 13.5026 0.063743 13.0084C0.0535292 12.5141 0.232112 12.0832 0.599489 11.7156L11.2419 1.07316C11.4225 0.892833 11.623 0.761013 11.8433 0.677701C12.0634 0.594389 12.2939 0.552734 12.5347 0.552734C12.7755 0.552734 13.0059 0.594389 13.226 0.677701C13.4464 0.761013 13.6469 0.892833 13.8275 1.07316L24.4792 11.7252C24.8264 12.0725 25 12.4967 25 12.9979C25 13.4991 24.8264 13.9335 24.4792 14.3012C24.1118 14.6686 23.6759 14.8522 23.1714 14.8522C22.667 14.8522 22.231 14.6686 21.8633 14.3012L14.3689 6.80635L14.3689 24.4018C14.3689 24.9164 14.1919 25.3509 13.838 25.7051C13.4838 26.059 13.0493 26.236 12.5347 26.236C12.02 26.236 11.5856 26.059 11.2314 25.7051C10.8775 25.3509 10.7005 24.9164 10.7005 24.4018L10.7005 6.80635Z" fill="#9CA3AF" />
                      <circle cx="12.5348" cy="29.5942" r="1.84284" transform="rotate(-180 12.5348 29.5942)" fill="#9CA3AF" />
                      <circle cx="12.5348" cy="35.1225" r="1.84284" transform="rotate(-180 12.5348 35.1225)" fill="#9CA3AF" />
                    </svg>

                  </div>
                </div>
                <div className="px-4">
                  {selectedLeadsForMerge.length === 0 || showSelectedLeads === false ?
                    <p className="font-normal mt-4 pt-1 text-center" style={{ color: "#9CA3AF" }}>
                      No leads selected. <span className="d-block pt-1">Please choose a lead from the list to merge
                      </span></p>
                    : <div>
                      <div className="mt-4 pb-5">
                        <ListTable
                          data={selectedLeadsForMerge}
                          sort={sort}
                        >
                          {selectedColumns}
                        </ListTable>
                      </div>
                    </div>}
                </div>
              </div>
              <div className="button-bottom-merge d-flex justify-content-end right-3" style={{ width: "49.4%" }}>
                <button className="btn btn-primary" onClick={() => {
                  setConfirmation(true)
                }}
                  disabled={selectedLeadsForMerge.length <= 0}
                >Merge Now</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {confirmation ? (
        <LeadMergeConfirmation
          onSubmit={onSubmit}
          setConfirmation={setConfirmation}
        />
      ) : (
        <></>
      )}
      <div className="modal-backdrop fade show"></div>
    </div>
  );
};

export default LeadMerge;
